import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { api } from '../lib/api';
import type { Project } from '../lib/projects';

interface Props {
  projectId: string;
  page: 'HOME' | 'SETUP' | 'GENERATOR' | 'POSTS' | 'TEAM' | 'SETTINGS' | 'INSPECTOR' | 'DASHBOARD' | 'BRAND' | 'GENERATE' | 'REVIEW' | 'PIPELINE';
  rightSlot?: React.ReactNode;
}

/**
 * Content-area header for every page inside a brand workspace. Carries the
 * `Bubbles // BRAND_NAME // PHASE` breadcrumb and an optional right slot
 * for page-specific controls (graph type toggle, filters, or post actions).
 *
 * No persistent action button — workflow CTAs live on the page they belong
 * to (e.g. "Create post" sits inside the GENERATE page, not orbiting
 * everything).
 */
export function ProjectHeader({ projectId, page, rightSlot }: Props) {
  const projectQuery = useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => api<{ project: Project }>(`/api/projects/${projectId}`).then((r) => r.project),
  });

  const project = projectQuery.data;
  const pageLabel = PAGE_LABELS[page] ?? page;

  return (
    <div className="px-4 md:px-6 py-3 md:py-4 border-b border-border-color bg-background-dark sticky top-0 z-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs font-semibold text-muted truncate min-w-0 flex-1">
          <Link
            href="/"
            className="text-accent-cyan hover:underline focus:outline-none focus:underline transition-colors"
            title="All brands"
          >
            Bubbles
          </Link>
          <span className="mx-2 opacity-50">//</span>
          <Link
            href={`/projects/${projectId}`}
            className="text-text-primary hover:text-accent-cyan focus:outline-none focus:text-accent-cyan transition-colors"
            title="Brand home"
          >
            {project?.name ?? 'Brand'}
          </Link>
          <span className="mx-2 opacity-50">//</span>
          <span>{pageLabel}</span>
        </p>
        {rightSlot && <div className="flex items-center gap-2 shrink-0">{rightSlot}</div>}
      </div>
    </div>
  );
}

const PAGE_LABELS: Record<Props['page'], string> = {
  HOME: 'Home',
  SETUP: 'Strategy',
  GENERATOR: 'Content',
  POSTS: 'Posts',
  TEAM: 'Team',
  SETTINGS: 'Settings',
  INSPECTOR: 'Post inspector',
  DASHBOARD: 'Home',
  BRAND: 'Strategy',
  GENERATE: 'Content',
  REVIEW: 'Posts',
  PIPELINE: 'Content',
};
